﻿<template>
  <div v-if="displayRoutes.length" class="user-routes-section">
    <!-- Expanded view - show icons in a row -->
    <div v-if="isExpanded" class="user-routes-icons-row" :class="{ isMobile: isMobile }">
      <div
        v-for="route in displayRoutes"
        :key="route.name"
        class="route-icon-item"
        @click="navigateToRoute(route)"
        @mouseenter="showToast(route)"
        @mouseleave="hideToast"
      >
        <IconRenderer
          size="small"
          :autoplay="false"
          :loop="false"
          :animateOnHover="true"
          type="ionic"
          :name="getRouteIcon(route)"
          class="route-icon-renderer"
        />
      </div>
      <!-- Toast for route name -->
      <div v-if="activeToast" class="route-toast" :class="{ visible: showRouteToast }">
        {{ $t(activeToast) }}
      </div>
    </div>

    <IonFab v-if="!isExpanded" horizontal="end" class="user-routes-fab">
      <IonFabButton
        size="small"
        @click="toggleFabMenu"
        class="main-fab-button"
        :class="{ 'fab-open': isFabOpen }"
      >
        <IonIcon :icon="isFabOpen ? closeOutline : closeOutline"></IonIcon>
      </IonFabButton>
      <IonFabList side="end" :class="{ 'fab-list-open': isFabOpen }">
        <IonFabButton
          v-for="route in displayRoutes"
          :key="route.name"
          size="small"
          @click="navigateToRoute(route)"
          @mouseenter="showToast(route)"
          @mouseleave="hideToast"
          class="route-fab-button"
        >
          <IconRenderer
            size="small"
            :autoplay="false"
            :loop="false"
            :animateOnHover="true"
            type="ionic"
            :name="getRouteIcon(route)"
            class="route-icon-renderer-fab"
          />
        </IonFabButton>
      </IonFabList>
      <!-- Toast for fab route name -->
      <div
        v-if="activeToast && !isExpanded"
        class="route-toast fab-toast"
        :class="{ visible: showRouteToast }"
      >
        {{ $t('menu.' + activeToast) }}
      </div>
    </IonFab>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { IonFab, IonFabButton, IonFabList, IonIcon } from '@ionic/vue';
import { closeOutline, appsOutline } from 'ionicons/icons';
import { CommonRoutesName, StudentRouteName } from '@/router/constants/routesName';
import { toCamelCase } from '@/common/utils/stringExtension';
import { useDeviceDetection } from '@/common/composables/useDeviceDetection';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

interface UserRoute {
  name: string;
  path: string;
  meta?: {
    title?: string;
    icon?: string;
  };
}

const props = withDefaults(
  defineProps<{
    userRoutes: UserRoute[];
    isExpanded?: boolean;
  }>(),
  {
    isExpanded: false,
  }
);

const emit = defineEmits<{
  (e: 'route-click', route: UserRoute): void;
}>();

const router = useRouter();
const { isMobile } = useDeviceDetection();

const activeToast = ref<string>('');
const showRouteToast = ref<boolean>(false);
const isFabOpen = ref<boolean>(false);
let toastTimeout: ReturnType<typeof setTimeout> | null = null;

const displayRoutes = computed(() => {
  const filterRoutes = props.userRoutes.filter(
    (route) =>
      !route.name.includes(StudentRouteName.AroundTheWorld) &&
      !route.name.includes(StudentRouteName.Chat) &&
      !route.name.includes(StudentRouteName.StudentHome) &&
      !route.name.includes(StudentRouteName.Shop) &&
      !route.name.includes(CommonRoutesName.PartnershipUniversities)
  );
  return filterRoutes;
});

function navigateToRoute(route: UserRoute) {
  if (route.path) {
    // Close fab menu if it's open
    isFabOpen.value = false;
    emit('route-click', route);
    router.push(route.path);
  }
}

function toggleFabMenu() {
  isFabOpen.value = !isFabOpen.value;
}

function handleClickOutside(event: Event) {
  const target = event.target as HTMLElement;
  if (!target.closest('.user-routes-fab')) {
    isFabOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

function showToast(route: UserRoute) {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  const routeName = route?.meta?.title ? route.meta.title : route.name;
  activeToast.value = routeName;
  showRouteToast.value = true;
}

function hideToast() {
  showRouteToast.value = false;
  toastTimeout = setTimeout(() => {
    activeToast.value = '';
  }, 200); // Delay to allow fade out animation
}

function getRouteIcon(route: UserRoute): string {
  // Return route-specific icon if available, otherwise default icons based on route name
  if (route.meta?.icon) {
    return route.meta.icon;
  }

  // Default icons based on common route names/patterns
  const iconMap: Record<string, string> = {
    dashboard: 'grid-outline',
    home: 'home-outline',
    profile: 'person-outline',
    settings: 'settings-outline',
    notifications: 'notifications-outline',
    messages: 'mail-outline',
    calendar: 'calendar-outline',
    documents: 'document-text-outline',
    help: 'help-circle-outline',
    support: 'help-buoy-outline',
    admin: 'shield-outline',
    partnerUniversities: 'shield-outline',
    students: 'school-outline',
    courses: 'book-outline',
    grades: 'bar-chart-outline',
    schedule: 'time-outline',
  };

  const routeName = toCamelCase(route.name);
  for (const [key, icon] of Object.entries(iconMap)) {
    if (routeName.includes(key)) {
      return icon;
    }
  }

  return 'apps-outline';
}
</script>

<style scoped>
.user-routes-section {
  padding: 15px 10px 20px 10px;
  margin-top: 25px;
  position: relative;
  display: flex;
  flex-wrap: wrap;
}

.route-icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(
    135deg,
    var(--ion-color-tertiary) 0%,
    var(--ion-color-tertiary-darker) 100%
  );
  min-width: 40px;
  min-height: 40px;
  position: relative;
}

.user-routes-icons-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  max-width: 300px;
  margin: 0 auto 10px auto;
}

.user-routes-icons-row.isMobile {
  max-width: 100%;
  width: 100%;
  width: 300px;
  justify-content: space-around;
}

.route-icon-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
}

.route-icon-item:hover {
  transform: scale(1.1);
}

.route-icon-item:hover::before {
  opacity: 1;
}

.route-icon-item:active {
  transform: translateY(-1px) scale(1.05);
}

.route-icon-renderer {
  color: var(--ion-icon-color-hover) !important;
  font-size: 20px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.route-icon-item:hover .route-icon-renderer {
  filter: brightness(1.2);
}

/* Toast styles */
.route-toast {
  position: absolute;
  left: 50%;
  top: -20px;
  background: rgba(193, 29, 29, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 1vmin;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.route-toast.visible {
  opacity: 1;
  transform: translateX(-50%) translateY(-5px);
}

.route-toast::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .user-routes-section {
    padding: 0;
    margin-top: 0;
  }
  .route-icon-item {
    min-width: 44px;
    min-height: 44px;
    padding: 10px;
  }

  .route-icon-renderer {
    font-size: 18px;
  }

  .route-toast {
    font-size: 11px;
    padding: 6px 10px;
  }
}

@media (max-width: 480px) {
  .user-routes-icons-row {
    gap: 6px;
    padding: 8px;
  }

  .route-icon-item {
    min-width: 40px;
    min-height: 40px;
    padding: 8px;
  }

  .route-icon-renderer {
    font-size: 16px;
  }
}

.user-routes-fab {
  z-index: 1000;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin-top: 30px;
}

.main-fab-button {
  --background: var(--ion-color-primary);
  --color: var(--ion-icon-color-primary-contrast);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --box-shadow: var(--ion-box-shadow);
  transform: rotate(45deg);
}

.main-fab-button.fab-open {
  transform: rotate(0deg);
}

.route-fab-button {
  --background: var(--ion-color-primary);
  --color: var(--ion-icon-color-primary-contrast);
  margin-bottom: 8px;
  transition: all 0.2s ease;
  --box-shadow: var(--ion-box-shadow);
}

.route-fab-button:hover {
  transform: scale(1.1);
  --box-shadow: var(--ion-box-shadow-hover);
}

.route-icon-renderer-fab {
  color: var(--ion-icon-color-primary-contrast) !important;
  font-size: 18px;
}

.fab-list-open {
  visibility: visible;
  opacity: 1;
}

.fab-toast {
  position: absolute;
  left: 190%;
  top: 50%;
  transform: translateX(-100%) translateY(-60px);
  background: rgba(193, 29, 29, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 1vmin;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.fab-toast.visible {
  opacity: 1;
  transform: translateX(-50%) translateY(-65px);
}
</style>
